import axios from "axios";
export var ContentType;
(function (ContentType) {
    ContentType["Json"] = "application/json";
    ContentType["FormData"] = "multipart/form-data";
    ContentType["UrlEncoded"] = "application/x-www-form-urlencoded";
    ContentType["Text"] = "text/plain";
})(ContentType || (ContentType = {}));
export class HttpClient {
    instance;
    securityData = null;
    securityWorker;
    secure;
    format;
    constructor({ securityWorker, secure, format, ...axiosConfig } = {}) {
        this.instance = axios.create({ ...axiosConfig, baseURL: axiosConfig.baseURL || "/helpdocumentation" });
        this.secure = secure;
        this.format = format;
        this.securityWorker = securityWorker;
    }
    setSecurityData = (data) => {
        this.securityData = data;
    };
    mergeRequestParams(params1, params2) {
        const method = params1.method || (params2 && params2.method);
        return {
            ...this.instance.defaults,
            ...params1,
            ...(params2 || {}),
            headers: {
                ...((method && this.instance.defaults.headers[method.toLowerCase()]) || {}),
                ...(params1.headers || {}),
                ...((params2 && params2.headers) || {}),
            },
        };
    }
    stringifyFormItem(formItem) {
        if (typeof formItem === "object" && formItem !== null) {
            return JSON.stringify(formItem);
        }
        else {
            return `${formItem}`;
        }
    }
    createFormData(input) {
        return Object.keys(input || {}).reduce((formData, key) => {
            const property = input[key];
            const propertyContent = property instanceof Array ? property : [property];
            for (const formItem of propertyContent) {
                const isFileType = formItem instanceof Blob || formItem instanceof File;
                formData.append(key, isFileType ? formItem : this.stringifyFormItem(formItem));
            }
            return formData;
        }, new FormData());
    }
    request = async ({ secure, path, type, query, format, body, ...params }) => {
        const secureParams = ((typeof secure === "boolean" ? secure : this.secure) &&
            this.securityWorker &&
            (await this.securityWorker(this.securityData))) ||
            {};
        const requestParams = this.mergeRequestParams(params, secureParams);
        const responseFormat = format || this.format || undefined;
        if (type === ContentType.FormData && body && body !== null && typeof body === "object") {
            body = this.createFormData(body);
        }
        if (type === ContentType.Text && body && body !== null && typeof body !== "string") {
            body = JSON.stringify(body);
        }
        return this.instance.request({
            ...requestParams,
            headers: {
                ...(requestParams.headers || {}),
                ...(type && type !== ContentType.FormData ? { "Content-Type": type } : {}),
            },
            params: query,
            responseType: responseFormat,
            data: body,
            url: path,
        });
    };
}
/**
 * @title Help documentation API
 * @baseUrl /helpdocumentation
 *
 * Documentation of help module API
 */
export class Api extends HttpClient {
    categories = {
        /**
         * No description
         *
         * @tags Categories
         * @name GetAll
         * @summary Returns all categories (Auth)
         * @request GET:/Categories
         * @secure
         */
        getAll: (params = {}) => this.request({
            path: `/Categories`,
            method: "GET",
            secure: true,
            format: "json",
            ...params,
        }),
    };
    helpItems = {
        /**
         * No description
         *
         * @tags HelpItems
         * @name GetAll
         * @summary Returns all help items for selected language. (Auth policies: EktacoUser)
         * @request GET:/HelpItems
         * @secure
         */
        getAll: (query, params = {}) => this.request({
            path: `/HelpItems`,
            method: "GET",
            query: query,
            secure: true,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags HelpItems
         * @name Update
         * @summary Edits help item. (Auth policies: EktacoUser)
         * @request PUT:/HelpItems
         * @secure
         */
        update: (updateBody, params = {}) => this.request({
            path: `/HelpItems`,
            method: "PUT",
            body: updateBody,
            secure: true,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags HelpItems
         * @name Add
         * @summary Adds new help item. (Auth policies: EktacoUser)
         * @request POST:/HelpItems
         * @secure
         */
        add: (addBody, params = {}) => this.request({
            path: `/HelpItems`,
            method: "POST",
            body: addBody,
            secure: true,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags HelpItems
         * @name Delete
         * @summary Removes help item. (Auth policies: EktacoUser)
         * @request DELETE:/HelpItems
         * @secure
         */
        delete: (query, params = {}) => this.request({
            path: `/HelpItems`,
            method: "DELETE",
            query: query,
            secure: true,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags HelpItems
         * @name GetAllActive
         * @summary Returns all active help items for selected language. (Auth)
         * @request GET:/HelpItems/Active
         * @secure
         */
        getAllActive: (query, params = {}) => this.request({
            path: `/HelpItems/Active`,
            method: "GET",
            query: query,
            secure: true,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags HelpItems
         * @name Get
         * @summary Returns help item by ID. (Auth policies: EktacoUser)
         * @request GET:/HelpItems/{id}
         * @secure
         */
        get: (id, params = {}) => this.request({
            path: `/HelpItems/${id}`,
            method: "GET",
            secure: true,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags HelpItems
         * @name GetActive
         * @summary Returns active help item by ID. (Auth)
         * @request GET:/HelpItems/Active/{id}
         * @secure
         */
        getActive: (id, params = {}) => this.request({
            path: `/HelpItems/Active/${id}`,
            method: "GET",
            secure: true,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags HelpItems
         * @name MarkAsRead
         * @summary Marks help items as read for current user. (Auth)
         * @request POST:/HelpItems/Read
         * @secure
         */
        markAsRead: (body, params = {}) => this.request({
            path: `/HelpItems/Read`,
            method: "POST",
            body: body,
            secure: true,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags HelpItems
         * @name SetOrder
         * @summary Set order for help items (Auth)
         * @request PUT:/HelpItems/Order
         * @secure
         */
        setOrder: (body, params = {}) => this.request({
            path: `/HelpItems/Order`,
            method: "PUT",
            body: body,
            secure: true,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
    };
    languages = {
        /**
         * No description
         *
         * @tags Languages
         * @name GetAll
         * @summary Returns all languages. (Auth)
         * @request GET:/Languages
         * @secure
         */
        getAll: (params = {}) => this.request({
            path: `/Languages`,
            method: "GET",
            secure: true,
            format: "json",
            ...params,
        }),
    };
    notifications = {
        /**
         * No description
         *
         * @tags Notifications
         * @name GetNewNotifications
         * @summary Returns new notifications with selected language for current user. (Auth)
         * @request GET:/Notifications
         * @secure
         */
        getNewNotifications: (query, params = {}) => this.request({
            path: `/Notifications`,
            method: "GET",
            query: query,
            secure: true,
            format: "json",
            ...params,
        }),
    };
}
